// Small shared pieces of the project screens: the status badge, the
// progress bar and the private-project lock.
import { Lock } from 'lucide-react'
import { Badge } from '../../components/ui/badge'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import type { ProjectStatus } from './api'

const STATUS_VARIANT = {
  on_track: 'success',
  at_risk: 'warning',
  off_track: 'destructive',
  on_hold: 'info',
  done: 'neutral',
} as const satisfies Record<ProjectStatus, string>

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  const t = useT(staffTasksI18n)
  return <Badge variant={STATUS_VARIANT[status]}>{t.projects.status[status]}</Badge>
}

/** The lock shown beside a private project's name; says how many see it. */
export function PrivateLock({
  memberCount,
  className = 'h-3.5 w-3.5',
}: {
  memberCount?: number
  className?: string
}) {
  const t = useT(staffTasksI18n)
  const label = memberCount ? t.privacy.lockTitle(memberCount) : t.privacy.badge
  return (
    <Lock
      className={`${className} shrink-0 text-[var(--color-text-muted)]`}
      role="img"
      aria-label={label}
      data-testid="staff-project-private-lock"
    >
      <title>{label}</title>
    </Lock>
  )
}

export function ProgressBar({
  done,
  total,
  label,
  className = 'w-24',
}: {
  done: number
  total: number
  label: string
  className?: string
}) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={done}
      className={`h-1.5 shrink-0 overflow-hidden rounded-full bg-[var(--color-surface-muted)] ${className}`}
    >
      <div
        className="h-full rounded-full bg-[var(--color-success)] transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  )
}
